import { AsyncPipe } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import { IOptionItem, SelectComponent } from '@ui-lib/components';
import { debounceTime, map, scan, shareReplay, startWith, Subject, switchMap, tap } from 'rxjs';
import { PoolDictionaryService } from '../pool-selector/pool-dictionary.service';
import { IDictionaryItem } from '../../types/dictionary-item.interface';
import { NoopValueAccessorDirective } from '../facility-select/noop-value-accessor.directive';

@Component({
  selector: 'mon-pool-select',
  templateUrl: './pool-select.component.html',
  styleUrl: './pool-select.component.scss',
  imports: [AsyncPipe, ReactiveFormsModule, SelectComponent],
  hostDirectives: [NoopValueAccessorDirective],
  providers: [PoolDictionaryService],
})
export class PoolSelectComponent {
  private readonly ngControl = inject(NgControl, { self: true, optional: true });

  protected readonly poolDictionaryService = inject(PoolDictionaryService);

  private loadedCount = 0;
  private readonly keyword$ = new Subject<string>();

  @Input()
  public labelTranslateKey = 'monitoring.pool';

  @Input()
  public searchTranslateKey = 'monitoring.search';

  @Input()
  public fullWidth = false;

  @Input()
  public isClearable = false;

  protected readonly poolOptions$ = this.keyword$.pipe(
    startWith(''),
    debounceTime(500),
    tap((keyword: string): void => {
      this.poolDictionaryService.updateKeyword(keyword.trim());
    }),
    switchMap(() =>
      this.poolDictionaryService.source$.pipe(
        scan(
          (
            acc: IDictionaryItem<string>[],
            newbies: IDictionaryItem<string>[],
          ): IDictionaryItem<string>[] => [...acc, ...newbies],
          [],
        ),
        map((items: IDictionaryItem<string>[]): IDictionaryItem<string>[] => {
          const seenIds = new Set<string>();

          return items.filter((item: IDictionaryItem<string>): boolean => {
            if (!item.id) return false;

            if (seenIds.has(item.id)) {
              return false;
            }

            seenIds.add(item.id);

            return true;
          });
        }),
        tap((items: IDictionaryItem<string>[]): void => {
          this.loadedCount = items.length;
        }),
        map((items): IOptionItem<string>[] =>
          items.map(
            (item): IOptionItem<string> => ({
              id: item.id,
              label: item.name,
            }),
          ),
        ),
      ),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public get control(): FormControl<IOptionItem<string> | null> {
    const control = this.ngControl?.control;

    if (!control) {
      throw new Error('mon-pool-select must be used with formControl or formControlName');
    }

    if (!(control instanceof FormControl)) {
      throw new Error('mon-pool-select supports only FormControl');
    }

    return control as FormControl<IOptionItem<string> | null>;
  }

  public loadMore(): void {
    if (this.loadedCount < this.poolDictionaryService.totalCount()) {
      this.poolDictionaryService.loadMore();
    }
  }

  public onKeywordChange(keyword: string): void {
    this.keyword$.next(keyword);
  }
}
